import { TrendingUp, DollarSign, PieChart, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart as RechartsPie, Pie, Cell } from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const revenueProjections = [
  { year: "Year 1", revenue: 95000, expenses: 72000, profit: 23000 },
  { year: "Year 2", revenue: 140000, expenses: 95000, profit: 45000 },
  { year: "Year 3", revenue: 195000, expenses: 125000, profit: 70000 },
  { year: "Year 4", revenue: 260000, expenses: 160000, profit: 100000 },
  { year: "Year 5", revenue: 340000, expenses: 195000, profit: 145000 },
];

const revenueBreakdown = [
  { name: "Swedish Massage", value: 30, color: "#0F172A" },
  { name: "Deep Tissue", value: 25, color: "#D4AF37" },
  { name: "Hot Stone", value: 15, color: "#64748B" },
  { name: "Sports Massage", value: 12, color: "#94A3B8" },
  { name: "Other Services", value: 18, color: "#CBD5E1" },
];

const fundUseData = [
  { category: "Equipment & Supplies", amount: 15000 },
  { category: "Marketing & Advertising", amount: 8000 },
  { category: "Facility Improvements", amount: 12000 },
  { category: "Working Capital", amount: 10000 },
  { category: "Training & Certifications", amount: 5000 },
];

const financialHighlights = [
  { label: "Projected Year 1 Revenue", value: "$95,000", icon: DollarSign },
  { label: "Year 5 Revenue Target", value: "$340,000", icon: TrendingUp },
  { label: "Gross Margin Target", value: "65%", icon: PieChart },
  { label: "5-Year ROI Projection", value: "258%", icon: BarChart3 },
];

export const FinancialsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="financials-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Financial Projections
            </p>
            <h1 className="section-heading text-5xl md:text-6xl font-bold mb-6">
              Investment Returns
            </h1>
            <p className="section-subheading mb-8">
              Detailed financial projections demonstrating the growth potential and 
              return on investment for Millennium Wellness Spa over a 5-year period.
            </p>
          </div>
        </div>
      </section>

      {/* Financial Highlights */}
      <section className="py-20 bg-white" data-testid="financial-highlights">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {financialHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={item.label}
                  className="stat-card animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`financial-highlight-${index}`}
                >
                  <Icon size={24} className="text-[#D4AF37] mb-3" />
                  <p className="text-3xl font-bold text-[#0F172A] mb-1">{item.value}</p>
                  <p className="text-sm text-slate-500">{item.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Revenue Projections Chart */}
      <section className="py-20 bg-slate-50" data-testid="revenue-chart">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                5-Year Projection
              </p>
              <h2 className="section-heading text-4xl font-semibold mb-6">
                Revenue Growth
              </h2>
              <p className="section-subheading mb-8">
                Our conservative projections show steady revenue growth based on 
                market analysis, service pricing, and client retention rates typical 
                for established massage therapy practices.
              </p>
              
              <div className="space-y-4">
                {revenueProjections.map((item, index) => (
                  <div 
                    key={item.year}
                    className="flex justify-between items-center p-4 bg-white border border-slate-100"
                    data-testid={`revenue-row-${index}`}
                  >
                    <span className="font-medium text-[#0F172A]">{item.year}</span>
                    <div className="flex gap-6 text-sm">
                      <span className="text-slate-500">Revenue: <span className="text-[#0F172A] font-semibold">${item.revenue.toLocaleString()}</span></span>
                      <span className="text-[#D4AF37] font-semibold">Profit: ${item.profit.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="chart-container h-[400px]">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Revenue vs Profit</h3>
              <ResponsiveContainer width="100%" height="90%">
                <LineChart data={revenueProjections}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="year" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} tickFormatter={(value) => `$${value/1000}k`} />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, '']}
                    contentStyle={{ borderRadius: '4px', border: '1px solid #E2E8F0' }}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#0F172A" strokeWidth={3} dot={{ fill: '#0F172A' }} name="Revenue" />
                  <Line type="monotone" dataKey="profit" stroke="#D4AF37" strokeWidth={3} dot={{ fill: '#D4AF37' }} name="Profit" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Breakdown & Fund Use */}
      <section className="py-20 bg-white" data-testid="breakdown-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Revenue Breakdown */}
            <div className="chart-container">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Revenue by Service</h3>
              <p className="text-sm text-slate-500 mb-6">Projected distribution of revenue across service categories</p>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={revenueBreakdown}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {revenueBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value}%`, '']} />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {revenueBreakdown.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-slate-600">{item.name} ({item.value}%)</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Fund Usage */}
            <div className="chart-container">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-2">Use of Funds</h3>
              <p className="text-sm text-slate-500 mb-6">How grant/investment funds will be allocated</p>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fundUseData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" stroke="#64748B" fontSize={12} tickFormatter={(value) => `$${value/1000}k`} />
                    <YAxis type="category" dataKey="category" stroke="#64748B" fontSize={11} width={140} />
                    <Tooltip 
                      formatter={(value) => [`$${value.toLocaleString()}`, '']}
                      contentStyle={{ borderRadius: '4px', border: '1px solid #E2E8F0' }}
                    />
                    <Bar dataKey="amount" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 p-4 bg-slate-50 border-l-4 border-[#D4AF37]">
                <p className="text-sm font-semibold text-[#0F172A]">Total Funding Request</p>
                <p className="text-2xl font-bold text-[#D4AF37]">$50,000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Assumptions */}
      <section className="py-20 bg-[#0F172A]" data-testid="assumptions">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Methodology
            </p>
            <h2 className="text-4xl font-semibold text-white mb-6 font-display">
              Key Assumptions
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3">Client Volume</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Year 1: 15-20 clients/week, growing to 35-40 clients/week by Year 5. 
                Based on single practitioner capacity and market demand.
              </p>
            </div>
            <div className="p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3">Average Ticket</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Average service price of $95, with annual 3-5% price increases. 
                Mix of 60-90 minute sessions across modalities.
              </p>
            </div>
            <div className="p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-3">Retention Rate</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                75% client retention rate based on industry standards and 
                Lacresha's track record of client loyalty over 16 years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="section-heading text-4xl font-semibold mb-6">
            Ready to Discuss Investment?
          </h2>
          <p className="section-subheading max-w-2xl mx-auto mb-8">
            Contact us to review detailed financial models and discuss partnership 
            opportunities with Millennium Wellness Spa.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-primary" data-testid="fin-contact-btn">
                Contact for Investment
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="h-12 px-8" data-testid="fin-dashboard-btn">
                Access Grant Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FinancialsPage;
